// src/components/PaymentFlow/PaymentFlow.js

import React, { useState, useEffect, useContext } from 'react';
import LoanList from './LoanList';
import PaymentSummary from './PaymentSummary';
import PaymentMethods from './PaymentMethods';
import './PaymentFlow.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../services/api';
import { formatNumber, parseLocaleNumber, formatCurrency } from '../../utils/numberFormatter';
import { faDownload, faPrint, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateAvailableCredit } from '../../utils/creditUtils';
import { ClientContext } from '../../context/ClientContext';
import { useProgress } from '../../context/ProgressContext';
import { PdfViewerModal } from '../Common';

const MySwal = withReactContent(Swal);

const PaymentFlow = ({ clientData, onClose }) => {
  const { setClientData } = useContext(ClientContext);
  const { setProgressText, reloadProgress } = useProgress();
  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [loadingInstallments, setLoadingInstallments] = useState({});
  const [selectedInstallments, setSelectedInstallments] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountToDistribute, setAmountToDistribute] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [changeDue, setChangeDue] = useState(0);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [summary, setSummary] = useState({
    total_debt: null,
    overdue_amount: 0,
    outstanding_balance: null
  });
  const [amountDisplay, setAmountDisplay] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [receiptDownloaded, setReceiptDownloaded] = useState(false);
  const [transferReceipt, setTransferReceipt] = useState(null);

  // Estados para el modal del PDF
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');

  // Cargar préstamos del cliente
    const fetchLoans = async () => {
      try {
        setLoadingLoans(true);
        const response = await api.get(`/customers/${clientData.id}/loans?load_installments=true`);
        
        // Guardamos el resumen
        setSummary({
          total_debt: response.data.summary.total_debt,
          overdue_amount: response.data.summary.overdue_amount,
          outstanding_balance: response.data.summary.outstanding_balance
        });

        const formattedLoans = response.data.loans.map(loan => ({
          id: loan.sale_id,
          uid: loan.loan.uid,
          issueDate: loan.loan.issued_date,
          totalDebt: loan.loan.financial_details.total_debt,
          overdueAmount: loan.loan.financial_details.overdue_amount,
          outstandingBalance: loan.loan.financial_details.outstanding_balance,
          product: loan.product,
          nextPayment: loan.loan.next_payment,
          status: loan.loan.status,
          installments: (loan.installments || []).map(inst => ({
            // Mantener estructura original para InstallmentRow
            number: inst.number,
            status: inst.status,
            dates: inst.dates,
            financial: inst.financial,
            balance: inst.balance,
            // Campos adicionales para compatibilidad con código existente
            installmentNumber: inst.number,
            dueDate: inst.dates.due_date,
            amount: inst.financial.total,
            paymentsMade: inst.balance.paid.total,
            amountDue: inst.balance.remain.total
          }))
        }));
        setLoans(formattedLoans);

      } catch (error) {
        console.error('Error fetching loans:', error);
        MySwal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los préstamos del cliente',
        });
      } finally {
        setLoadingLoans(false);
      }
    };

  // Cargar préstamos al montar el componente
  useEffect(() => {
    if (clientData?.id) {
      fetchLoans();
    }
  }, [clientData]);

  // Función para cargar las cuotas de un préstamo
  const fetchInstallments = async (loanId, loanUid) => {
    try {
      setLoadingInstallments(prev => ({ ...prev, [loanId]: true }));
      const response = await api.get(`/customers/${clientData.id}/loans/${loanUid}/installments`);
      
      const formattedInstallments = response.data.installments.map(inst => ({
        // Mantener estructura original para InstallmentRow
        number: inst.number,
        status: inst.status,
        dates: inst.dates,
        financial: inst.amounts ? {
          // Si viene con 'amounts', convertir a 'financial'
          total: inst.amounts.total,
          capital: inst.amounts.capital || 0,
          punitives: inst.amounts.punitives || { total: 0 },
          interest: inst.amounts.interest || { total: 0 },
          extra_fees: inst.amounts.extra_fees || { total: 0 }
        } : inst.financial, // Si ya viene con 'financial', usarlo directamente
        balance: inst.balances ? {
          // Si viene con 'balances', convertir a 'balance'
          paid: inst.balances.paid,
          remain: inst.balances.remaining
        } : inst.balance, // Si ya viene con 'balance', usarlo directamente
        // Campos adicionales para compatibilidad con código existente
        installmentNumber: inst.number,
        dueDate: inst.dates.due_date,
        amount: inst.amounts?.total || inst.financial?.total,
        paymentsMade: inst.balances?.paid?.total || inst.balance?.paid?.total,
        amountDue: inst.balances?.remaining?.total || inst.balance?.remain?.total
      }));

      setLoans(prevLoans => 
        prevLoans.map(loan => 
          loan.id === loanId 
            ? { ...loan, installments: formattedInstallments }
            : loan
        )
      );
    } catch (error) {
      console.error('Error fetching installments:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar las cuotas del préstamo',
      });
    } finally {
      setLoadingInstallments(prev => ({ ...prev, [loanId]: false }));
    }
  };

  // Update total amount whenever selectedInstallments changes
  useEffect(() => {
    const total = selectedInstallments.reduce((sum, item) => sum + parseFloat(item.amount), 0);
    setTotalAmount(total);
  }, [selectedInstallments]);

  // Handle installment selection
  const handleInstallmentSelection = (loanId, installmentIndex, amount) => {
    setSelectedInstallments((prev) => {
      const newInstallments = [...prev];
      const existingIndex = newInstallments.findIndex(
        (item) => item.loanId === loanId && item.installmentIndex === installmentIndex
      );

      if (amount > 0) {
        if (existingIndex >= 0) {
          newInstallments[existingIndex].amount = amount;
        } else {
          const loan = loans.find((l) => l.id === loanId);
          const installment = loan.installments[installmentIndex];
          newInstallments.push({
            loanId,
            installmentIndex,
            installment,
            amount,
          });
        }
      } else {
        if (existingIndex >= 0) {
          newInstallments.splice(existingIndex, 1);
        }
      }
      return newInstallments;
    });
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');

    if (value === '') {
      setAmountDisplay('');
      setAmountToDistribute('');
      setTotalAmount(0);
      return;
    }

    // Asegurarse de que solo haya una coma
    const parts = value.split(',');
    if (parts.length > 2) {
      // Si hay más de una coma, mantener solo la primera
      const formattedValue = parts[0] + ',' + parts.slice(1).join('');
      setAmountDisplay(formattedValue);
      setAmountToDistribute(parseLocaleNumber(formattedValue).toString());
      return;
    }

    setAmountDisplay(value);
    const numericValue = parseLocaleNumber(value);
    
    if (!isNaN(numericValue)) {
      setAmountToDistribute(numericValue.toString());
      setTotalAmount(numericValue);
    }
  };

  // Calcular el monto total a pagar (para el resumen)
  const totalAmountToPay = parseFloat(amountToDistribute) || 0;

  // Manejar la confirmación del pago
  const handleConfirmPayment = () => {
    if (!paymentMethod) {
      MySwal.fire({
        icon: 'warning',
        title: 'Medio de pago requerido',
        text: 'Por favor, seleccione un medio de pago.',
      });
      return;
    }

    if (totalAmountToPay <= 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Monto inválido',
        text: 'Por favor, ingrese un monto válido para pagar.',
      });
      return;
    }

    // Calcular el saldo total disponible
    // Usa el máximo entre total_debt (incluye punitorios) y outstanding_balance
    const totalOutstandingBalance = loans.reduce((sum, loan) => {
      const maxDebt = Math.max(
        parseFloat(loan.totalDebt) || 0, 
        parseFloat(loan.outstandingBalance) || 0
      );
      return sum + maxDebt;
    }, 0);

    // Validar que el monto no supere el saldo total
    if (totalAmountToPay > totalOutstandingBalance) {
      MySwal.fire({
        icon: 'error',
        title: 'Monto Excedido',
        text: `El monto que intenta pagar, excede el saldo total de los préstamos (${formatCurrency(totalOutstandingBalance)})`,
      });
      return;
    }

    // Inicializar el modal de pago en efectivo
    setCashReceived(totalAmountToPay.toString());
    setChangeDue(0);
    setShowCashModal(true);
  };

  // Manejar cambios en el efectivo recibido
  const handleCashReceivedChange = (e) => {
    const value = e.target.value;
    setCashReceived(value);
    
    const receivedAmount = parseFloat(value) || 0;
    setChangeDue(receivedAmount - totalAmountToPay);
  };

  // Función para distribuir el pago entre los préstamos - NUEVA VERSIÓN POR CUOTAS
  const distributePayment = (amount, loans) => {
    console.log('=== INICIO distributePayment ===');
    console.log('Monto a distribuir:', amount);
    console.log('Cantidad de préstamos:', loans.length);
    
    // Helper: obtiene el máximo deuda pagable de un préstamo
    // totalDebt incluye punitorios cuando hay mora, outstandingBalance es el capital pendiente
    const getMaxDebt = (loan) => Math.max(
      parseFloat(loan.totalDebt) || 0,
      parseFloat(loan.outstandingBalance) || 0
    );

    // Verificar si los préstamos tienen cuotas cargadas
    const loansWithInstallments = loans.filter(
      loan => loan.installments && loan.installments.length > 0
    );

    console.log('Préstamos con cuotas cargadas:', loansWithInstallments.length);
    loansWithInstallments.forEach(loan => {
      console.log(`Préstamo ${loan.id}: ${loan.installments.length} cuotas`);
    });

    // Si no hay cuotas cargadas, usar distribución por préstamo (fallback)
    if (loansWithInstallments.length === 0) {
      console.warn('⚠️ No se encontraron cuotas cargadas, usando distribución por préstamo');
      
      if (loans.length === 0) {
        console.error('❌ No hay préstamos disponibles');
        return {
          payment_type: "loan",
          payment_method: paymentMethod,
          amount: amount,
          loans: []
        };
      }

      if (loans.length === 1) {
        const loan = loans[0];
        const maxDebt = getMaxDebt(loan);
        console.log(`Préstamo único ${loan.id}, deuda máxima: ${maxDebt}`);
        
        return {
          payment_type: "loan",
          payment_method: paymentMethod,
          amount: amount,
          loans: [{
            loanId: loan.id,
            amount: Math.min(amount, maxDebt)
          }]
        };
      }

      // Distribuir al préstamo más antiguo
      const sortedLoans = [...loans].sort((a, b) => 
        new Date(a.issueDate) - new Date(b.issueDate)
      );
      
      const oldestLoan = sortedLoans[0];
      const maxDebt = getMaxDebt(oldestLoan);
      console.log(`Préstamo más antiguo ${oldestLoan.id}, deuda máxima: ${maxDebt}`);
      
      return {
        payment_type: "loan",
        payment_method: paymentMethod,
        amount: amount,
        loans: [{
          loanId: oldestLoan.id,
          amount: Math.min(amount, maxDebt)
        }]
      };
    }

    // NUEVA LÓGICA: Distribución por cuotas
    console.log('📋 Recolectando cuotas...');
    
    // 1. Recolectar TODAS las cuotas de TODOS los préstamos
    const allInstallments = [];
    
    loansWithInstallments.forEach(loan => {
      console.log(`Procesando préstamo ${loan.id}:`, loan);
      
      loan.installments.forEach((installment, index) => {
        const amountDue = parseFloat(installment.amountDue) || 0;
        console.log(`  Cuota ${installment.installmentNumber}: amountDue=${amountDue}, status=${installment.status?.code}`);
        
        // Solo incluir cuotas con saldo pendiente
        if (amountDue > 0) {
          allInstallments.push({
            loanId: loan.id,
            loanUid: loan.uid,
            installmentNumber: installment.installmentNumber,
            installmentIndex: index,
            dueDate: installment.dueDate,
            amountDue: amountDue,
            status: installment.status,
            loanIssueDate: loan.issueDate,
            overdueAmount: parseFloat(loan.overdueAmount) || 0
          });
          console.log(`    ✅ Cuota agregada (${amountDue} pendiente)`);
        } else {
          console.log(`    ⏭️ Cuota omitida (sin saldo pendiente)`);
        }
      });
    });

    console.log(`Total de cuotas con saldo pendiente: ${allInstallments.length}`);

    // 2. Ordenar cuotas por criterio de prioridad
    console.log('🔄 Ordenando cuotas por prioridad...');
    allInstallments.sort((a, b) => {
      // Prioridad 1: Estado de la cuota (vencidas/morosas primero)
      const statusPriority = {
        4: 1, // Vencida/Morosa
        2: 2, // Pago parcial
        0: 3, // Activa
        1: 4  // Pendiente
      };
      const aPriority = statusPriority[a.status.code] || 99;
      const bPriority = statusPriority[b.status.code] || 99;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // Prioridad 2: Fecha de vencimiento (más antigua primero)
      const dateA = new Date(a.dueDate.split('/').reverse().join('-'));
      const dateB = new Date(b.dueDate.split('/').reverse().join('-'));
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // Prioridad 3: Fecha de emisión del préstamo (más antiguo primero)
      const issueDateA = new Date(a.loanIssueDate);
      const issueDateB = new Date(b.loanIssueDate);
      if (issueDateA.getTime() !== issueDateB.getTime()) {
        return issueDateA - issueDateB;
      }

      // Prioridad 4: ID del préstamo (menor = más antiguo en el sistema)
      // Esto asegura distribución equitativa cuando hay empate en fechas
      if (a.loanId !== b.loanId) {
        return a.loanId - b.loanId;
      }

      // Prioridad 5: Número de cuota (menor primero)
      return a.installmentNumber - b.installmentNumber;
    });

    // Log del orden final para debug
    console.log('📋 Orden de cuotas a pagar:');
    allInstallments.slice(0, 5).forEach((inst, idx) => {
      console.log(`  ${idx + 1}. Préstamo ${inst.loanId}, Cuota ${inst.installmentNumber}: $${inst.amountDue.toFixed(2)} (vence: ${inst.dueDate})`);
    });
    if (allInstallments.length > 5) {
      console.log(`  ... y ${allInstallments.length - 5} cuotas más`);
    }

    // 3. Distribuir el pago secuencialmente por cuotas
    let remainingAmount = amount;
    const paymentByLoan = {}; // Agrupar por loanId para mantener estructura del backend
    
    for (const installment of allInstallments) {
      if (remainingAmount <= 0) break;
      
      const amountToApply = Math.min(remainingAmount, installment.amountDue);
      
      // Agrupar por loanId para el backend
      if (!paymentByLoan[installment.loanId]) {
        paymentByLoan[installment.loanId] = {
          loanId: installment.loanId,
          amount: 0
        };
      }
      
      paymentByLoan[installment.loanId].amount += amountToApply;
      remainingAmount -= amountToApply;
      
      // Log para debug (puedes comentarlo en producción)
      console.log(`Aplicando $${amountToApply.toFixed(2)} a Préstamo ${installment.loanId}, Cuota ${installment.installmentNumber}`);
    }

    // 4. Validar que no se exceda la deuda máxima de cada préstamo
    const paymentDistribution = Object.values(paymentByLoan).map(payment => {
      const loan = loansWithInstallments.find(l => l.id === payment.loanId);
      const maxDebt = getMaxDebt(loan);
      
      return {
        loanId: payment.loanId,
        amount: Math.min(payment.amount, maxDebt)
      };
    });

    console.log('📦 Distribución final:', paymentDistribution);

    // 5. Validación de seguridad: si no hay distribución, abortar
    if (paymentDistribution.length === 0) {
      console.error('❌ ERROR: No se pudo generar distribución de pago');
      console.error('Préstamos disponibles:', loans);
      console.error('Cuotas encontradas:', allInstallments);
      
      MySwal.fire({
        icon: 'error',
        title: 'Error en distribución',
        html: 'No se pudo distribuir el pago. Posibles causas:<br>- No hay cuotas con saldo pendiente<br>- Error en la carga de datos<br><br>Por favor, recarga la página e intenta nuevamente.',
      });
      
      return {
        payment_type: "loan",
        payment_method: paymentMethod,
        amount: amount,
        loans: []
      };
    }

    // 6. Retornar en el formato esperado por el backend
    console.log('=== FIN distributePayment ===');
    return {
      payment_type: "loan",
      payment_method: paymentMethod,
      amount: amount,
      loans: paymentDistribution
    };
  };

  // Modificar handlePaymentConfirm para recargar los préstamos
  const handlePaymentConfirm = async () => {
    setIsProcessing(true);
    try {
      const paymentData = distributePayment(totalAmount, loans);

      // Validación: no continuar si no hay distribución
      if (!paymentData.loans || paymentData.loans.length === 0) {
        console.error('❌ No se generó distribución de pago válida, abortando...');
        setIsProcessing(false);
        return;
      }

      console.log('📤 Enviando pago al backend:', paymentData);

      let response;
      if (paymentMethod === 'bank_transfer' && transferReceipt) {

        const formData = new FormData();
        formData.append('payment_type', paymentData.payment_type);
        formData.append('payment_method', paymentData.payment_method);
        formData.append('amount', paymentData.amount);
        formData.append('loans', JSON.stringify(paymentData.loans));
        formData.append('voucher', transferReceipt);

        response = await api.post(
          `/customers/${clientData.id}/payments`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        response = await api.post(
          `/customers/${clientData.id}/payments`,
          paymentData
        );
      }
      
      // Verificar si la respuesta tiene el formato esperado
      if (response.data && response.data.payments && Array.isArray(response.data.payments)) {
        // Actualizar el crédito disponible
        await updateAvailableCredit(clientData, setClientData);
        setProgressText('Disponible');
        reloadProgress();
        
        // Crear HTML para mostrar los comprobantes
        const receiptsHtml = response.data.payments.map(payment => `
          <div class="mb-3">
            <p class="mb-1">Comprobante #${payment.uid}</p>
            <div class="btn-group">
              <button 
                onclick="window.handleViewReceipt('${payment.receipt_url}', 'Comprobante #${payment.uid}')" 
                class="btn btn-outline-primary btn-sm">
                <i class="bi bi-eye me-1"></i>Ver Comprobante
              </button>
            </div>
          </div>
        `).join('');

        // Mostrar modal de éxito con los comprobantes
        await MySwal.fire({
        icon: 'success',
        title: '¡Pago Exitoso!',
          html: `
            <div class="text-center">
              <p class="mb-3">El pago ha sido registrado correctamente.</p>
              <div class="receipts-container">
                ${receiptsHtml}
              </div>
            </div>
          `,
        showConfirmButton: true,
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#3085d6',
          didOpen: () => {
            // Agregar el manejador de eventos para ver comprobantes
            window.handleViewReceipt = (url, title) => {
              setPdfUrl(url);
              setPdfTitle(title);
              setShowPdfModal(true);
              MySwal.close();
            };
          },
          willClose: () => {
            // Limpiar el manejador de eventos al cerrar el modal
            delete window.handleViewReceipt;
          }
        });

        // Cerrar el modal de efectivo y limpiar todos los estados
        setShowCashModal(false);
        setPaymentReceipt(null);
        setReceiptDownloaded(false);
        setCashReceived('');
        setChangeDue(0);
        
        // Recargar los préstamos
        await fetchLoans();
        
        // Limpiar el monto ingresado
        setAmountDisplay('');
        setAmountToDistribute('');
        setTotalAmount(0);
      } else {
        throw new Error('Formato de respuesta inválido');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      
      // Manejar diferentes tipos de errores
      let errorMessage = 'Hubo un error al procesar el pago.';
      let errorDetails = '';
      
      if (error.response) {
        switch (error.response.status) {
          case 422:
            errorMessage = error.response.data.message || 'Error de validación en los datos del pago.';
            // Procesar los errores de validación
            if (error.response.data.errors) {
              errorDetails = Object.values(error.response.data.errors)
                .flat()
                .join('<br>');
            }
            break;
          case 500:
            errorMessage = 'Error interno del servidor. Por favor, intente nuevamente.';
            break;
          default:
            errorMessage = error.response.data.message || errorMessage;
        }
      }

      MySwal.fire({
        icon: 'error',
        title: 'Error',
        html: errorDetails 
          ? `${errorMessage}<br><br><small class="text-danger">${errorDetails}</small>`
          : errorMessage,
        confirmButtonColor: '#d33',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Función para redondear al siguiente múltiplo de 1000
  const roundToNextThousand = (amount) => {
    return Math.ceil(amount / 1000) * 1000;
  };

  // Efecto para inicializar valores cuando se muestra el modal de efectivo
  useEffect(() => {
    if (showCashModal) {
      const roundedAmount = roundToNextThousand(totalAmount);
      setCashReceived(roundedAmount.toString());
      setChangeDue(roundedAmount - totalAmount);
    }
  }, [showCashModal]);

  // Distribute amount entered by the user
  const distributeAmount = () => {
    resetFields();

    const amount = parseFloat(amountToDistribute);
    if (isNaN(amount) || amount <= 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Datos inválidos',
        text: 'Por favor, ingresa un monto válido para distribuir.',
      });
      return;
    }

    let allInstallments = [];
    loans.forEach((loan) => {
      loan.installments.forEach((installment, index) => {
        allInstallments.push({
          loanId: loan.id,
          installmentIndex: index,
          installment,
        });
      });
    });

    allInstallments.sort((a, b) => {
      const dateA = new Date(a.installment.dueDate.split('/').reverse().join('-'));
      const dateB = new Date(b.installment.dueDate.split('/').reverse().join('-'));
      return dateA - dateB;
    });

    const totalPending = allInstallments.reduce(
      (sum, item) => sum + parseFloat(item.installment.amountDue),
      0
    );
    if (amount > totalPending) {
      MySwal.fire({
        icon: 'warning',
        title: 'Datos inválidos',
        text: 'El monto a distribuir es mayor al total pendiente.',
      });
      return;
    }

    let remainingAmount = amount;
    const newSelectedInstallments = [];

    for (let item of allInstallments) {
      if (remainingAmount <= 0) break;
      const { loanId, installmentIndex, installment } = item;
      const amountDue = parseFloat(installment.amountDue);
      let amountToPay = 0;

      if (remainingAmount >= amountDue) {
        amountToPay = amountDue;
        remainingAmount -= amountDue;
      } else {
        amountToPay = remainingAmount;
        remainingAmount = 0;
      }

      newSelectedInstallments.push({
        loanId,
        installmentIndex,
        installment,
        amount: amountToPay,
      });
    }

    setSelectedInstallments(newSelectedInstallments);

    const updatedLoans = loans.map((loan) => {
      const updatedInstallments = loan.installments.map((installment, index) => {
        const selected = newSelectedInstallments.find(
          (item) => item.loanId === loan.id && item.installmentIndex === index
        );
        return {
          ...installment,
          amountToPay: selected ? selected.amount : '',
          isChecked: !!selected,
          inputDisabled: selected ? false : index !== 0,
          checkboxDisabled: selected ? false : index !== 0,
        };
      });
      return {
        ...loan,
        installments: updatedInstallments,
      };
    });

    setLoans(updatedLoans);
  };

  // Reset all fields before distributing amount
  const resetFields = () => {
    setSelectedInstallments([]);
    const updatedLoans = loans.map((loan) => {
      const updatedInstallments = loan.installments.map((installment, index) => {
        return {
          ...installment,
          amountToPay: '',
          isChecked: false,
          inputDisabled: index !== 0,
          checkboxDisabled: index !== 0,
        };
      });
      return {
        ...loan,
        installments: updatedInstallments,
      };
    });
    setLoans(updatedLoans);
  };

  const handleLoadTotalDebt = () => {
    setAmountDisplay(formatNumber(summary.total_debt.toString(),2));
    setAmountToDistribute(summary.total_debt.toString());
    setTotalAmount(parseFloat(summary.total_debt) || 0);
  };

  const handleDownloadReceipt = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `comprobante-${paymentReceipt.uid}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      setReceiptDownloaded(true);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al descargar el comprobante.',
      });
    }
  };

  const handlePrintReceipt = (url) => {
    setPdfUrl(url);
    setPdfTitle('Comprobante de Pago');
    setShowPdfModal(true);
    setReceiptDownloaded(true);
  };

  useEffect(() => {
    if (!showCashModal) {
      setTransferReceipt(null);
    }
  }, [showCashModal]);

  return (
    <div className="payment-flow">
      <section className="section">
        <div className="row m-0">
          <div className="col-9 ps-0">
            <div className="container mt-0">
              <div className="d-flex align-items-center mb-3">
                <h4 className="mb-0">
                  <i className="bi bi-credit-card me-2 text-primary"></i>
                  Cobrar Cuotas
                </h4>
              </div>
              <div className="card">
                <div className="card-header p-3">
                  <div className="row align-items-center">
                    <div className="col-4">

                      {summary.total_debt !== null && (
                        <>
                          <h5 className="mb-0">
                            Saldo: <span className="text-primary">
                              {formatCurrency(Math.max(summary.total_debt || 0, summary.outstanding_balance || 0), 2)}
                            </span>
                          </h5>
                        {summary.overdue_amount > 0 && (
                          <p className="mb-0 text-danger">
                            <small>
                              Saldo vencido: {formatCurrency(summary.overdue_amount, 2)}
                            </small>
                          </p>
                        )}
                        </>
                      )}
                    </div>
                    <div className="col-8 pe-1">
                      <div className="float-end">
                        <div className="input-group">
                          <span className="input-group-text bg-transparent border-end-0">$</span>
                          <input 
                            type="text"
                            className="form-control border-start-0" 
                            placeholder="Ingrese monto" 
                            value={amountDisplay} 
                            onChange={handleAmountChange} 
                          />
                          {/* <button 
                            className="btn btn-primary" 
                            onClick={distributeAmount}
                          > 
                            Distribuir 
                          </button> */}
                          <button 
                            className="btn btn-outline-primary" 
                            onClick={handleLoadTotalDebt}
                            title="Cargar saldo total"
                          >
                            <i className="bi bi-cash-coin"></i>
                          </button>
                        </div>
                        {Math.max(summary.total_debt || 0, summary.outstanding_balance || 0) > 0 && (
                          <small className="text-muted mt-1 d-block text-end">
                            Click en <i className="bi bi-cash-coin"></i> para cargar el saldo total
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body position-relative p-2">
                  <div className="overflow-auto" style={{ maxHeight: '400px', paddingBottom: '10px' }}>
                    <LoanList
                      loans={loans}
                      clientId={clientData.id}
                      onInstallmentSelection={handleInstallmentSelection}
                      loading={loadingLoans}
                      loadingInstallments={loadingInstallments}
                      onLoadInstallments={fetchInstallments}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-3 pe-0 ps-0">
            <div id="payment-summary" className="card">
              <div className="card-header text-center">
                <h4 className="mb-0">Resumen de Pago</h4>
              </div>
              <div className="card-body p-0">
                <PaymentSummary selectedInstallments={selectedInstallments} />
                <h6 className="text-muted text-center">Selecciona medio de pago</h6>
                <PaymentMethods
                  selectedMethod={paymentMethod}
                  onMethodSelect={setPaymentMethod}
                />
                <div className="mt-2 text-center">
                  <h6>
                    Total a Pagar:{' '}
                    <span className="text-primary">
                      {formatCurrency(totalAmount,2)}
                    </span>
                  </h6>
                  {(() => {
                    const maxDebt = Math.max(summary.total_debt || 0, summary.outstanding_balance || 0);
                    if (totalAmount > maxDebt && maxDebt > 0) {
                      return (
                        <small className="mt-1 d-block small text-success">
                          <i className="bi bi-info-circle me-1"></i>
                          Está adelantando {formatCurrency(totalAmount - maxDebt, 2)}
                        </small>
                      );
                    } else if (Math.abs(totalAmount - maxDebt) < 0.01 && maxDebt > 0) {
                      return (
                        <small className="mt-1 d-block small text-success">
                          <i className="bi bi-info-circle me-1"></i>
                          Está quedando al día
                        </small>
                      );
                    }
                    return null;
                  })()}
                  <button
                    id="confirmPaymentBtn"
                    className="btn btn-success w-100 mt-3"
                    disabled={
                      totalAmount <= 0 || 
                      !paymentMethod || 
                      !process.env.REACT_APP_ENABLED_PAYMENT_METHODS
                    }
                    onClick={handleConfirmPayment}
                  >
                    Confirmar Pago
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {showQRModal && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: 'block' }}
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Pago con QR</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowQRModal(false)}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <p>Monto a pagar: {formatCurrency(totalAmount)}</p>
                  <img
                    src="/assets/compiled/png/qr-demo.png"
                    width={300}
                    alt="QR Code"
                  />
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowQRModal(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => {}}
                  >
                    Confirmar Pago
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showCashModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                {!paymentReceipt ? (
                  <>
                    <div className="modal-header">
                      <h5 className="modal-title">Confirmar Pago</h5>
                      <button type="button" className="btn-close" onClick={() => setShowCashModal(false)}></button>
                    </div>
                    <div className="modal-body py-2">
                      <div className="mb-4">
                        <span className="fw-semibold">Medio de pago:</span>
                        <div className="mt-1">
                          <span className="px-2 fs-4 fw-bold text-primary">
                            {paymentMethod === 'cash' && 'Efectivo'}
                            {paymentMethod === 'bank_transfer' && 'Transferencia Bancaria'}
                          </span>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="form-label fw-semibold">Monto a cobrar:</label>
                        <div className="d-flex justify-content-start">
                          <span className="px-2 display-6 text-primary">{formatCurrency(totalAmount,2)}</span>
                        </div>
                      </div>
                      {paymentMethod === 'cash' && (
                        <>
                          <div className="mb-4">
                            <label htmlFor="cashReceived" className="form-label fw-semibold">Efectivo recibido:</label>
                            <div className="input-group input-group-lg px-2" style={{ maxWidth: 250 }}>
                              <span className="input-group-text bg-transparent border-end-0">$</span>
                              <input
                                type="number"
                                className={`form-control border-start-0 ${changeDue < 0 ? 'is-invalid' : ''}`}
                                id="cashReceived"
                                value={cashReceived}
                                onChange={handleCashReceivedChange}
                                min={totalAmount}
                                aria-label="Efectivo recibido"
                              />
                            </div>
                            {changeDue < 0 && (
                              <div className="invalid-feedback">
                                El monto recibido es menor al total a pagar
                              </div>
                            )}
                          </div>
                          <div className="mb-4">
                            <label className="form-label fw-semibold">Vuelto a entregar:</label>
                            <div className="d-flex justify-content-start">
                              <span className={`px-2 display-6 ${changeDue >= 0 ? 'text-success' : 'text-danger'}`}>
                                {formatCurrency(Math.max(changeDue, 0),2)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                      {paymentMethod === 'bank_transfer' && (
                        <div className="mb-4">
                          <label className="form-label fw-semibold" htmlFor="transferReceipt">
                            Comprobante de transferencia
                          </label>
                          <div
                            className={`transfer-receipt-dropzone ${transferReceipt ? 'uploaded' : ''}`}
                            onClick={() => document.getElementById('transferReceipt').click()}
                            style={{ userSelect: 'none' }}
                          >
                            <span className="transfer-receipt-icon">
                              <i className="bi bi-file-earmark-text"></i>
                            </span>
                            <span className="transfer-receipt-name flex-grow-1">
                              {transferReceipt ? transferReceipt.name : 'Arrastra el archivo aquí o haz clic para seleccionarlo'}
                            </span>
                            <span className="transfer-receipt-status">
                              {transferReceipt ? (
                                <i className="bi bi-check-circle-fill text-success"></i>
                              ) : (
                                <i className="bi bi-clock"></i>
                              )}
                            </span>
                            <input
                              type="file"
                              id="transferReceipt"
                              accept="image/*,application/pdf"
                              style={{ display: 'none' }}
                              onChange={e => setTransferReceipt(e.target.files[0])}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="modal-footer justify-content-end">
                      <button 
                        className="btn btn-outline-secondary px-4" 
                        onClick={() => setShowCashModal(false)}
                        disabled={isProcessing}
                        aria-label="Cancelar"
                      >
                        Cancelar
                      </button>
                      <button
                        className="btn btn-success px-4 ms-2"
                        onClick={handlePaymentConfirm}
                        disabled={
                          changeDue < 0 ||
                          isProcessing
                        }
                        aria-label="Confirmar Pago"
                      >
                        {isProcessing ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Procesando...
                          </>
                        ) : (
                          'Confirmar Pago'
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="modal-header">
                      <h5 className="modal-title">Comprobante de Pago</h5>
                      <button type="button" className="btn-close" onClick={() => setShowCashModal(false)}></button>
                    </div>
                    <div className="modal-body">
                      <div className="text-center mb-4">
                        <i className="fas fa-check-circle text-success" style={{ fontSize: '48px' }}></i>
                        <h4 className="mt-2">¡Pago Registrado!</h4>
                        <p className="text-muted">Comprobante #{paymentReceipt.uid}</p>
                      </div>
                      <div className="d-flex justify-content-center mb-3">
                        <div className="btn-group">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handlePrintReceipt(paymentReceipt.receipt_url)}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Ver Comprobante
                          </button>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button 
                          className="btn btn-primary" 
                          onClick={onClose}
                        >
                          Cerrar
                        </button>
                      </div>

                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal para vista previa de PDF */}
      <PdfViewerModal
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        url={pdfUrl}
        title={pdfTitle}
      />
    </div>
  );
};

export default PaymentFlow;
